import type { InstalacionDeInforme } from "@/lib/informes";
import { numeroEscrito } from "@/lib/numeroEscrito";

import type { FormularioDeInstalacion } from "./instalaciones";
import { pluralizar } from "./registroDeTerreno";

/* ---------------------------------------------------------------------------
   La lista de instalaciones: lo que se DERIVA de las filas del informe.

   Es lógica PURA y vive fuera del componente por la razón de siempre: qué se
   ve bajo un filtro o cuánto cuenta un chip son las cosas que pueden estar mal
   sin que nada se vea roto, y dentro del árbol un test no distingue una cifra
   buena de una cercana.

   ES MÁS CORTA QUE SU GEMELA DE CONSTRUCCIONES a propósito: el diseño de esta
   pestaña no trae cruce con la visita de terreno, ni orden, ni paginación. Las
   filas se ven en el orden en que se cargaron.
--------------------------------------------------------------------------- */

export type FiltroDeInstalaciones = "todas" | "sin_valorizar" | "sin_validar";

const FILTROS: FiltroDeInstalaciones[] = ["todas", "sin_valorizar", "sin_validar"];

/**
 * Una fila SIN NINGUNO de los dos totales.
 *
 * Con uno solo ya está valorizada: los dos valores son independientes —el de
 * reposición es lo que cuesta hacer la obra y el comercial lo que aporta al
 * predio— y un documento puede traer solo uno. Contarla como pendiente
 * mandaría a buscar un dato que quizás no existe.
 */
export function estaSinValorizar(fila: InstalacionDeInforme): boolean {
  return fila.total_reposicion_uf === null && fila.total_comercial_uf === null;
}

function coincide(fila: InstalacionDeInforme, filtro: FiltroDeInstalaciones): boolean {
  switch (filtro) {
    case "todas":
      return true;
    case "sin_valorizar":
      return estaSinValorizar(fila);
    case "sin_validar":
      return !fila.validada;
  }
}

export interface ResumenDeInstalaciones {
  cantidad: number;
  sinValorizar: number;
  validadas: number;
  /** La suma de los totales que calculó el servidor; lo que falta no entra como cero. */
  totalReposicion: number;
  totalComercial: number;
}

export interface VistaDeInstalaciones {
  /** Lo que se dibuja: ya filtrado y buscado, en el orden del servidor. */
  visibles: InstalacionDeInforme[];
  /** El conteo de cada filtro. NO depende del filtro ni de la búsqueda puestos. */
  conteos: Record<FiltroDeInstalaciones, number>;
  resumen: ResumenDeInstalaciones;
}

export function vistaDeInstalaciones(
  filas: InstalacionDeInforme[],
  { busqueda, filtro }: { busqueda: string; filtro: FiltroDeInstalaciones },
): VistaDeInstalaciones {
  const q = busqueda.trim().toLowerCase();

  const visibles = filas
    .filter((f) => coincide(f, filtro))
    .filter((f) => q === "" || f.tipo.toLowerCase().includes(q));

  const conteos = Object.fromEntries(
    FILTROS.map((k) => [k, filas.filter((f) => coincide(f, k)).length]),
  ) as Record<FiltroDeInstalaciones, number>;

  return {
    visibles,
    conteos,
    resumen: {
      cantidad: filas.length,
      sinValorizar: conteos.sin_valorizar,
      validadas: filas.filter((f) => f.validada).length,
      totalReposicion: aDosDecimales(filas.reduce((n, f) => n + (f.total_reposicion_uf ?? 0), 0)),
      totalComercial: aDosDecimales(filas.reduce((n, f) => n + (f.total_comercial_uf ?? 0), 0)),
    },
  };
}

/** "4 instalaciones y obras", o el nombre de la lista si no hay ninguna. */
export function tituloDeLaListaDeInstalaciones(cantidad: number): string {
  return cantidad === 0
    ? "Instalaciones y obras"
    : pluralizar(cantidad, "instalación u obra", "instalaciones y obras");
}

/**
 * La bajada del encabezado. NO SUMA CANTIDADES, al revés que los metros
 * construidos de construcciones: acá cada fila mide en su propia unidad —m² de
 * losa, metros lineales de cierro, unidades de estanque— y sumarlas daría una
 * cifra sin unidad.
 */
export function textoDelResumenDeInstalaciones(resumen: ResumenDeInstalaciones): string {
  if (resumen.cantidad === 0) {
    return "Agrega la primera instalación u obra o usa Rellenar con IA.";
  }

  const sin = resumen.sinValorizar > 0 ? ` · ${resumen.sinValorizar} sin valorizar` : "";

  return `Valores en UF por unidad${sin}`;
}

/**
 * Los dos totales que el servidor va a calcular con lo escrito: el GEMELO de
 * `InformeInstalacion::porLaCantidad()`.
 *
 * Cada uno es su unitario por la cantidad y NINGUNO SE DERIVA DEL OTRO. Sin
 * cantidad no hay ninguno: una obra se mide, y suponer un "1" daría el precio
 * unitario con cara de total. Cambiar la cuenta de un lado obliga a cambiarla
 * del otro en el mismo commit, o la vista previa diría una cifra distinta de
 * la que queda guardada.
 */
export function totalesDeLaInstalacion(
  formulario: Pick<
    FormularioDeInstalacion,
    "cantidad" | "valor_reposicion_uf_un" | "valor_comercial_uf_un"
  >,
): { reposicion: number | null; comercial: number | null } {
  const cantidad = numeroEscrito(formulario.cantidad);
  const porLaCantidad = (unitario: string) => {
    const valor = numeroEscrito(unitario);

    return valor === null || cantidad === null ? null : aDosDecimales(valor * cantidad);
  };

  return {
    reposicion: porLaCantidad(formulario.valor_reposicion_uf_un),
    comercial: porLaCantidad(formulario.valor_comercial_uf_un),
  };
}

/** `round($x, 2)` de PHP: mitades hacia afuera del cero, simétrico en negativos. */
function aDosDecimales(valor: number): number {
  return (Math.sign(valor) * Math.round(Math.abs(valor) * 100 + 1e-9)) / 100;
}
